<template>
<div class="Profile basicForm">
  <div class="PersonForm">
    <h3>{{ $t('form.person.block_name') }}</h3>

    <div v-if="loaded">
      <form>
        <div class="form-row">
          <div class="form-item form-item-20" :class="{ hasError: showErrors && !person.sex }">
            <label >{{ $t('form.person.fld_sex') }} *</label>
            <select name="person.sex" v-model="person.sex" required>
              <option disabled value='null'>{{ $t('form.person.fld_sex_option_null') }}</option>
              <option value="f">{{ $t('form.person.fld_sex_option_f') }}</option>
              <option value="m">{{ $t('form.person.fld_sex_option_m') }}</option>
            </select>
          </div>
          <div class="form-item form-item-40" :class="{ hasError: showErrors && !person.firstname }">
            <label>{{ $t('form.person.fld_firstname') }} *</label>
            <input name="person.firstname" type="text" v-model="person.firstname" required/>
          </div>
          <div class="form-item form-item-40" :class="{ hasError: showErrors && !person.lastname }">
            <label>{{ $t('form.person.fld_lastname') }} *</label>
            <input name="person.lastname" type="text" v-model="person.lastname" required/>
          </div>
        </div>

        <BirthdateField v-model="person.birthdate" type="person" :error="birthdateError" :class="{ hasError: showErrors && !person.birthdate }"/>

      </form>
    </div>
  </div>

  <div class="AddressForm">
    <h3>{{ $t('form.address.block_name') }}</h3>
    <div v-if="loaded">
      <AddressGeocode :address="address" :showErrors="showErrors" />
    </div>
  </div>

  <div class="KidsForm border m-2 p-2">
    <h3>{{ $t('form.kids.block_name') }}</h3>
    <div v-if="loaded">
      <form>
        <div v-for="(kid, i) in kids" :key="i">
          <h4 v-if="kids.length > 1">
            <span>{{ $t('form.kids.kid_heading', { cnt: i + 1 }) }}</span>
            <a v-if="kidValid(kid)"
               style="margin-left:80%;cursor:pointer;"
               @click.prevent="removeKid(i)">
              {{ $t('form.kids.btn_remove_kid') }}
            </a>
          </h4>

          <BirthdateField v-model="kid.birthdate" type="kid" :error="birthdateErrorKid(kid)" v-if="loaded" :key="i" :class="{ hasError: showErrors && !kid.birthdate }"/>

          <div class="form-row">
            <div class="form-item form-item-20" :class="{ hasError: isRequiredByAge(kid) && !kid.sex }">
              <label>{{ $t('form.kids.fld_sex') }}<span v-if="isRequiredByAge(kid)" style="display:inline;"> *</span></label>
              <select :name="'kids.sex_' + i"
                      v-model="kid.sex"  :required="isRequiredByAge(i)">
                <option disabled value='null'>{{ $t('form.kids.fld_sex_option_null') }}</option>
                <option value="f">{{ $t('form.kids.fld_sex_option_f') }}</option>
                <option value="m">{{ $t('form.kids.fld_sex_option_m') }}</option>
                <option value="?">{{ $t('form.kids.fld_sex_option_unknown') }}</option>
              </select>
            </div>

            <div class="form-item form-item-40" :class="{ hasError: isRequiredByAge(kid) && !kid.firstname }">
              <label class="small">{{ $t('form.kids.fld_firstname') }}<span v-if="isRequiredByAge(kid)" style="display:inline;"> *</span></label>
              <input
                :name="'kids.firstname_' + i"
                type="text"
                :required="isRequiredByAge(i)"
                v-model="kid.firstname"/>
            </div>

            <div class="form-item form-item-40">
              <label>{{ $t('form.kids.fld_lastname') }}</label>
              <input
                :name="'kids.last_' + i"
                type="text"
                v-model="kid.lastname"/>
            </div>
          </div>

          <div v-if="kids.length > 1 && !kidValid(kid)" class="buttonAlignRight">
            <button @click.prevent="removeKid(i)" class="btn-tertiary">{{ $t('form.kids.btn_cancel') }}</button>
          </div>

        </div>
        <div v-if="allValid" class="buttonAlignRight">
          <button name="addKid"
                  class="btn-secondary"
                  @click.prevent="addKid()">{{ $t('form.kids.btn_add_kid') }}</button>
        </div>
      </form>
    </div>
  </div>

  <div class="TelephoneForm border m-2 p-2">
    <h3>{{ $t('form.telephone.block_name') }}</h3>
    <div v-if="loaded">
      <TelephoneInput :showErrors="true" :isRequired="false" :value="telephones[0].tnumber || ''" :countryPrefix="telephones[0].prefix" v-on:upd:telephone="updateTel($event)"/>
    </div>
  </div>

  <img v-if="axiosBusy" src="static/letsfamily-de/img/spinner.svg"/>
  <div v-if="showErrors" class="alert alert-notice">
    {{ $t('form.profile.invalid_heading') }}
    <ul v-if="errors.length">
      <li v-for="(err,i) in errors" :key="i">{{ $t(err) }}</li>
    </ul>
  </div>
  <div class="buttonAlignRight">
    <button v-if="continue_uri" class="btn-primary" v-on:click="doSaveAndRedirect()" >
      {{ $t('form.generic.btn_save_continue') }}
    </button>
    <button v-else class="btn-primary" v-on:click="doSave()" >
      {{ $t('form.generic.btn_save') }}
    </button>
  </div>
</div>

</template>

<script>

import BirthdateField from '@/snippets/BirthdateField.vue'
import TelephoneInput from '@/snippets/TelephoneInput.vue';
import AddressGeocode from '@/snippets/AddressGeocode.vue';

import { mapGetters } from "vuex";
import { getProfile, updateProfile } from "@/api/profile.js";
import { parseISO, isBefore, subYears, addMonths, differenceInMonths } from "date-fns";

export default {
  name: 'Profile',
  components: {
    BirthdateField,
    TelephoneInput,
    AddressGeocode
  },
  data: function() { return {
    googleApiKey: this.$parent.googleApiKey,
    continue_uri: this.$parent.continue_uri,
    loaded: false,
    person: {},
    address: {},
    kids: [],
    telephones: [],
    hasData: false,
    showErrors: false,
    errors: [],
    timeout: undefined,
  }},
  computed: {
    ...mapGetters({
      axiosBusy: "axiosIsBusy"
    }),
    allValid() {
      if (this.personValid && this.addressAllValid && this.kidsAllValid) {
        return true;
      } else {
        return false;
      }
    },
    personValid() {
      if (this.hasAllRequiredPerson && this.birthdateValid) {
        return true;
      } else {
        return false;
      }
    },
    hasAllRequiredPerson() {
      if (
        this.person.sex &&
          this.person.birthdate &&
          this.person.firstname &&
          this.person.lastname
      ) {
        return true;
      } else {
        return false;
      }
    },
    kidsAllValid() {
      let okKid = 0;
      this.kids.forEach(k => {
        if (this.kidValid(k)) {
          okKid++
        }
      });
      if (this.kids.length == okKid) {
        return true;
      } else {
        return false;
      }
    },
    birthdateError() {
      if (this.person.birthdate === null || this.person.birthdate === undefined) {
        // no date yet, so don`t check for errors
        return false;
      }
      if (this.person.birthdate) {
        const bd = parseISO(this.person.birthdate);
        if (isBefore(subYears(new Date(), 16), bd)) {
          return "person_too_young";
        }
        if (isBefore(bd, subYears(new Date(), 100))) {
          return "person_too_old";
        }
        return false;
      } else {
        return "invalid";
      }
    },
    birthdateValid() {
      return this.birthdateError ? false : true;
    },
    addressAllValid() {
      if ( this.address.country &&
           this.address.locality &&
           this.address.street.length >= 3 &&
           this.address.streetnumber &&
           this.address.zip && this.address.zip.length >= 3
         ) {
        return true;
      } else {
        return false;
      }
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      const rv = await getProfile(this.$userClient);
      if (rv) {
        this.person = rv.profile.person || {};
        this.address = rv.profile.address || {};
        this.kids = rv.profile.kids || [{birthdate:"", sex: "", firstname:"", lastname:""}];
        this.telephones = rv.profile.telephones || [{tnumber:null}];
        this.loaded = true;
        this.hasData = this.allValid;
      }
    },
    async doSave() {
      this.checkFrontendErrors();
      await this.updProfile();
    },
    async doSaveAndRedirect() {
      await this.doSave();
      if (!this.showErrors) {
        window.location = this.continue_uri;
      }
    },
    checkFrontendErrors() {
      this.errors = [];
      if (!this.personValid) {
        this.errors.push('form.person.block_is_incomplete');
      }
      if (!this.addressAllValid) {
        this.errors.push('form.address.block_is_incomplete');
      }
      if (!this.kidsAllValid) {
        this.errors.push('form.kids.block_is_incomplete');
      }
      this.showErrors = this.errors.length > 0 ? true : false;
      return this.showErrors;
    },
    async updProfile() {
      const rv = await updateProfile(this.$userClient, {
        person: this.person,
        address: this.address,
        kids: this.kids,
        telephones: this.telephones
      });
      if (rv) {
        this.showErrors = this.checkFrontendErrors();
        this.person = rv.person.person;
        this.address = rv.address.address;
        this.kids = rv.kids.kids;
        this.telephones = rv.telephones.telephones;
      }
    },
    addKid() {
      this.kids.push({
        firstname: "",
        lastname: "",
        sex: "",
        birthdate: null
      });
    },
    async removeKid(i) {
      if (this.kids.length == 1) {
        this.kids[0].firstname = "";
        this.kids[0].lastname = "";
        this.kids[0].birthdate = null;
        this.kids[0].sex = "";
      } else {
        this.$delete(this.kids, i);
      }
      await this.updProfile();
    },
    kidValid(kid) {
      if (kid.birthdate && this.birthdateErrorKid(kid) == false) {
        if (this.isRequiredByAge(kid)) {
          return (kid.firstname  && kid.sex ) ? true : false;
        }
        return true;
      }
      return false;
    },
    birthdateErrorKid(kid) {
      if (kid.birthdate === null || kid.birthdate === undefined ||  kid.birthdate === '') {
        // no date yet, so don't check for errors
        return false;
      }
      if (kid.birthdate) {
        const bd = parseISO(kid.birthdate);
        if (isBefore(addMonths(new Date(), 10), bd)) {
          return "kid_too_young";
        }
        if (isBefore(bd, subYears(new Date(), 18))) {
          return "kid_too_old";
        }
        return false;
      } else {
        return "invalid";
      }
    },
    isRequiredByAge(kid) {
      if (kid.birthdate === null) {
        return false;
      }
      if (kid.birthdate) {
        let bd = parseISO(kid.birthdate);
        return differenceInMonths(new Date(), bd) >= 1 ? true : false;
      }
      return false;
    },
    telValid(tel) {
      return (tel && tel.tnumber && tel.tnumber.length > 5 && tel.tnumber.length <= 20) ? true : false
    },
    updateTel(tel) {
      this.telephones[0].tnumber = tel;
    },
  }
}
</script>
